import React, { useEffect, useMemo, useRef, useState } from "react";
import { Collection, fetchCollections } from "../../api";
import { useAuth0 } from "@auth0/auth0-react";
import { tourStepsTimedChallenges } from "./tourStepsTimedChallenges";
import GuidedTour from "../../components/GuidedTour";
import { useTour } from "../../context/TourContext";
import { useNavigate } from "react-router-dom";
import BubbleText from "../../components/BubbleText";
import GamePageToolbar from "../../components/GamePageToolbar";
import { useCompletion } from "../../context/CompletionContext";
import {
  CollectionItemData,
  parseCollectionItems,
} from "../../utils/parseCollectionItems";
import {
  ColorblindImage,
  ColorblindImageContainer,
} from "../../components/ColorblindImage";
import { isNumberSenseQuestion } from "../../utils/RandomGenerators";

const SESSION_SECONDS = 60;
const SESSION_ITEM_COUNT = 60;

const shuffleItems = (items: CollectionItemData[]) => {
  const next = [...items];
  for (let index = next.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(Math.random() * (index + 1));
    [next[index], next[swapIndex]] = [next[swapIndex], next[index]];
  }
  return next;
};

const getCollectionItems = (collection: Collection): CollectionItemData[] => {
  const fromDescription = parseCollectionItems(collection.description);
  if (fromDescription.length > 0) {
    return fromDescription;
  }

  return (collection.items ?? []).map((item) => ({
    name: item.name,
    svg: item.svg,
    count: item.count,
  }));
};

const buildSessionItems = (source: CollectionItemData[]) => {
  if (source.length === 0) {
    return [];
  }

  const session: CollectionItemData[] = [];
  while (session.length < SESSION_ITEM_COUNT) {
    session.push(...shuffleItems(source));
  }
  return session.slice(0, SESSION_ITEM_COUNT);
};

const TimedChallenges: React.FC = () => {
  const { getAccessTokenSilently } = useAuth0();
  const [collections, setCollections] = useState<Collection[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { isGuidedTourEnabled, isTourRunning, setIsTourRunning } = useTour();
  const [currentTourStep, setCurrentTourStep] = useState(0);
  const navigate = useNavigate();
  const { updateCompletionCount } = useCompletion();
  const hasRecordedCompletion = useRef(false);

  const [selectedCollectionId, setSelectedCollectionId] = useState<number | null>(
    null,
  );
  const [isGameStarted, setIsGameStarted] = useState(false);
  const [isGameCompleted, setIsGameCompleted] = useState(false);
  const [sessionItems, setSessionItems] = useState<CollectionItemData[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [correctCount, setCorrectCount] = useState(0);
  const [incorrectCount, setIncorrectCount] = useState(0);
  const [timeLeft, setTimeLeft] = useState(SESSION_SECONDS);

  const steps = tourStepsTimedChallenges();

  useEffect(() => {
    const loadCollections = async () => {
      try {
        const fetchedCollections = await fetchCollections(
          getAccessTokenSilently,
        );
        setCollections(
          (fetchedCollections ?? []).filter(
            (collection: Collection) => collection.collection_id != null,
          ),
        );
      } catch (error) {
        console.error("Error loading collections:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadCollections();
  }, [getAccessTokenSilently]);

  useEffect(() => {
    if (!isGameStarted || isGameCompleted) {
      return;
    }

    const intervalId = window.setInterval(() => {
      setTimeLeft((currentTime) => Math.max(0, currentTime - 1));
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, [isGameStarted, isGameCompleted]);

  useEffect(() => {
    if (isGameStarted && timeLeft === 0 && !isGameCompleted) {
      setIsGameCompleted(true);
    }
  }, [isGameStarted, isGameCompleted, timeLeft]);

  useEffect(() => {
    if (
      !isGameCompleted ||
      hasRecordedCompletion.current ||
      selectedCollectionId == null
    ) {
      return;
    }

    hasRecordedCompletion.current = true;
    updateCompletionCount(selectedCollectionId, {
      total_items: correctCount + incorrectCount,
      time_taken: SESSION_SECONDS - timeLeft,
      missed_items: 0,
      skipped_items: incorrectCount,
    });
  }, [
    isGameCompleted,
    selectedCollectionId,
    correctCount,
    incorrectCount,
    timeLeft,
    updateCompletionCount,
  ]);

  useEffect(() => {
    if (!isGameStarted || isGameCompleted) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.repeat) {
        return;
      }
      if (event.code === "Space" || event.key === "Enter") {
        event.preventDefault();
        markItem(true);
      } else if (event.key === "Backspace" || event.key.toLowerCase() === "x") {
        event.preventDefault();
        markItem(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isGameStarted, isGameCompleted, currentIndex, sessionItems.length]);

  const selectedCollection = useMemo(
    () =>
      collections.find(
        (collection) => collection.collection_id === selectedCollectionId,
      ) ?? null,
    [collections, selectedCollectionId],
  );

  const selectedItemCount = selectedCollection
    ? getCollectionItems(selectedCollection).length
    : 0;

  const startGame = () => {
    if (!selectedCollection || selectedItemCount === 0) {
      return;
    }

    setSessionItems(buildSessionItems(getCollectionItems(selectedCollection)));
    setCurrentIndex(0);
    setCorrectCount(0);
    setIncorrectCount(0);
    setTimeLeft(SESSION_SECONDS);
    setIsGameCompleted(false);
    setIsGameStarted(true);
    hasRecordedCompletion.current = false;
  };

  const markItem = (wasCorrect: boolean) => {
    if (!isGameStarted || isGameCompleted) {
      return;
    }

    if (wasCorrect) {
      setCorrectCount((count) => count + 1);
    } else {
      setIncorrectCount((count) => count + 1);
    }

    if (currentIndex + 1 >= sessionItems.length) {
      setIsGameCompleted(true);
      return;
    }

    setCurrentIndex((index) => index + 1);
  };

  const goBackToSetup = () => {
    setIsGameStarted(false);
    setIsGameCompleted(false);
    setSessionItems([]);
    setCurrentIndex(0);
    setCorrectCount(0);
    setIncorrectCount(0);
    setTimeLeft(SESSION_SECONDS);
  };

  const handleTourComplete = () => {
    setIsTourRunning(false);
    localStorage.setItem("tourCompleted_timedChallenges", "true");
  };

  const handleBack = () => {
    navigate("/games");
  };

  const currentItem = sessionItems[currentIndex];
  const attemptedCount = correctCount + incorrectCount;
  const canStart = selectedCollectionId !== null && selectedItemCount > 0;

  const renderChallengeItem = (item: CollectionItemData) => {
    const prompt = isNumberSenseQuestion(item.name) ? (
      <p className="mb-3 text-center text-xl font-bold">{item.name}</p>
    ) : null;

    if (item.svg) {
      if (
        item.svg.startsWith("data:image") ||
        item.svg.startsWith("data:application")
      ) {
        return (
          <div className="flex flex-col items-center">
            {prompt}
            <ColorblindImage
              src={item.svg}
              alt={item.name}
              className="max-h-64 max-w-full object-contain"
            />
          </div>
        );
      }

      return (
        <div className="flex flex-col items-center">
          {prompt}
          <ColorblindImageContainer className="max-h-64 max-w-full">
            <div dangerouslySetInnerHTML={{ __html: item.svg }} />
          </ColorblindImageContainer>
        </div>
      );
    }

    if (item.name.startsWith("data:image")) {
      return (
        <ColorblindImage
          src={item.name}
          alt="Collection item"
          className="max-h-64 max-w-full object-contain"
        />
      );
    }

    return (
      <p className="max-w-[90vw] break-words text-center text-[clamp(2rem,8vmin,3.75rem)] font-bold">
        {item.name}
      </p>
    );
  };

  return (
    <div className={`page-container text-theme-text`}>
      <GamePageToolbar onBack={handleBack}>
        {isGameStarted && (
          <>
            <button
              type="button"
              onClick={startGame}
              className="reset-button max-w-[300px] cursor-pointer rounded border border-black bg-yellow-500 px-3 py-2 text-base font-bold uppercase text-black transition duration-300 hover:scale-105 hover:bg-yellow-600 active:scale-95 active:bg-yellow-700"
            >
              Try Again
            </button>
            <button
              type="button"
              onClick={goBackToSetup}
              className="back-to-setup-button max-w-[300px] cursor-pointer rounded border border-black bg-blue-500 px-3 py-2 text-base font-bold uppercase text-white transition duration-300 hover:bg-blue-600"
            >
              Back to Setup
            </button>
          </>
        )}
      </GamePageToolbar>
      <h1 className="mb-8 text-3xl font-bold">
        <BubbleText>Timed Challenges</BubbleText>
      </h1>

      {!isGameStarted ? (
        <div className="game-instructions flex w-full max-w-3xl flex-col items-center">
          <p className="inherit mb-6 text-center">
            Pick a collection and see how many items you can correctly identify
            in 60 seconds. The goal is 60 items in a minute.
          </p>
          {isLoading ? (
            <p className="inherit">Loading collections...</p>
          ) : collections.length === 0 ? (
            <p className="inherit text-center">
              You need a collection to play. Create one first, then come back
              here.
            </p>
          ) : (
            <div className="collections-overview mb-6 w-full">
              <h2 className="inherit mb-3 text-xl font-semibold">
                Your Collections
              </h2>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {collections.map((collection) => {
                  const itemCount = getCollectionItems(collection).length;
                  const isSelected =
                    collection.collection_id === selectedCollectionId;

                  return (
                    <button
                      key={collection.collection_id}
                      type="button"
                      onClick={() =>
                        setSelectedCollectionId(collection.collection_id)
                      }
                      className={`rounded border-2 p-3 text-left transition duration-200 ${
                        isSelected
                          ? "border-blue-600 bg-blue-100 text-black"
                          : "border-black bg-white text-black hover:border-blue-400"
                      }`}
                    >
                      <p className="font-semibold">{collection.name}</p>
                      <p className="text-sm">
                        {itemCount} {itemCount === 1 ? "item" : "items"}
                      </p>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          <button
            type="button"
            onClick={startGame}
            disabled={!canStart}
            className={`start-button max-w-[300px] rounded border border-black bg-green-500 p-2.5 text-base font-bold uppercase text-white transition duration-300 hover:bg-green-600 ${
              canStart ? "cursor-pointer" : "cursor-not-allowed opacity-50"
            }`}
          >
            Start Challenge
          </button>
        </div>
      ) : (
        <div className="timed-challenge-game flex w-full max-w-3xl flex-col items-center">
          <div className="mb-6 grid w-full grid-cols-1 gap-4 text-center sm:grid-cols-3">
            <div>
              <p className="text-sm uppercase">Time</p>
              <p className="text-3xl font-bold">{timeLeft}s</p>
            </div>
            <div>
              <p className="text-sm uppercase">Correct</p>
              <p className="text-3xl font-bold">{correctCount}</p>
            </div>
            <div>
              <p className="text-sm uppercase">Goal</p>
              <p className="text-3xl font-bold">{SESSION_ITEM_COUNT}</p>
            </div>
          </div>

          {isGameCompleted ? (
            <div className="text-center">
              <h2 className="mb-4 text-2xl font-semibold">Session Report</h2>
              <p className="mb-2">
                Items shown: {attemptedCount}
              </p>
              <p className="mb-2">
                Time taken: {SESSION_SECONDS - timeLeft} seconds
              </p>
              <p className="mb-2">
                Missed items: 0
              </p>
              <p className="inherit mb-2">
                Skipped items: {incorrectCount}
              </p>
              <p className="inherit">
                Correct: {correctCount} of {SESSION_ITEM_COUNT}
              </p>
            </div>
          ) : (
            currentItem && (
              <>
                <div className="mb-8 flex min-h-[min(280px,40vh)] w-full items-center justify-center rounded-lg border border-black bg-white p-6 text-black">
                  {renderChallengeItem(currentItem)}
                </div>
                <div className="flex flex-wrap justify-center gap-4">
                  <button
                    type="button"
                    onClick={() => markItem(true)}
                    className="rounded border border-black bg-green-500 px-6 py-3 text-lg font-bold uppercase text-white hover:bg-green-600"
                  >
                    Got it
                  </button>
                  <button
                    type="button"
                    onClick={() => markItem(false)}
                    className="rounded border border-black bg-red-500 px-6 py-3 text-lg font-bold uppercase text-white hover:bg-red-600"
                  >
                    Skip
                  </button>
                </div>
                <p className="inherit mt-4 text-sm">
                  Item {currentIndex + 1} of {SESSION_ITEM_COUNT}
                </p>
                <p className="mt-2 text-sm text-gray-500">
                  Space or Enter = Got it. X = Skip.
                </p>
              </>
            )
          )}
        </div>
      )}

      <GuidedTour
        steps={steps}
        isRunning={isTourRunning && isGuidedTourEnabled}
        onComplete={handleTourComplete}
        currentStep={currentTourStep}
        onStepChange={setCurrentTourStep}
        tourName="timedChallenges"
      />
    </div>
  );
};

export default TimedChallenges;
